import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GuideCard } from './guide-card';
import { CardGrid, Section } from './section';
import { seedScript } from './seed-script';
import { localePath } from '../lib/format';
import { GUIDES_PATH, fetchRelatedGuides, relatedGuidesSeedKey, type GuideKey } from '../lib/guides';
import { useArticles } from '@todo/core/hooks/use-articles';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { LIST_BUCKET, articlesIndexSeedKey } from '@todo/core/list-queries';
import type { Article } from '@todo/core/sanity/queries';
import { getPrefetched } from '@todo/core/static-prefetch';

/** The home page's "From the guides": the newest few. */
const LATEST_GUIDES = 3;

/** The page's "Read also" seed, for its <PageHead> (a function, as seedScript). */
export function relatedGuidesSeed(page: string, locale: Locale) {
  return seedScript(relatedGuidesSeedKey(page, locale));
}

/**
 * The page's "Read also" cards: the build's seed (../lib/guides.ts), baked
 * into the HTML so the links are in the static page; without one (a dev
 * server, a client-side navigation) fetched from Sanity.
 */
function useRelatedGuides(page: string, guides: readonly GuideKey[] | undefined, locale: Locale): Article[] {
  const seedKey = relatedGuidesSeedKey(page, locale);
  const seed = getPrefetched<Article[]>(LIST_BUCKET, seedKey);
  const [fetched, setFetched] = useState<{ key: string; cards: Article[] } | null>(null);

  useEffect(() => {
    if (!guides?.length || getPrefetched<Article[]>(LIST_BUCKET, seedKey) !== undefined) return;
    let cancelled = false;
    fetchRelatedGuides(guides, locale)
      .then((cards) => {
        if (!cancelled) setFetched({ key: seedKey, cards });
      })
      .catch(() => {
        // No block rather than an error: the page is complete without it.
      });
    return () => {
      cancelled = true;
    };
  }, [seedKey, guides, locale]);

  return seed ?? (fetched?.key === seedKey ? fetched.cards : []);
}

/**
 * "Read also": the guides a collection or hub page links to, in the page's
 * language (`guides` from COLLECTIONS / HUB_GUIDES). Nothing when none has a
 * version in it.
 */
export function RelatedGuides({ page, guides }: { page: string; guides: readonly GuideKey[] | undefined }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const cards = useRelatedGuides(page, guides, locale);
  if (!guides?.length || !cards.length) return null;
  return (
    <Section
      id="read-also"
      title={t('guides.readAlso')}
      more={{ href: localePath(locale, GUIDES_PATH), label: t('guides.all') }}>
      <CardGrid>
        {cards.map((a) => (
          <GuideCard key={a._id} article={a} />
        ))}
      </CardGrid>
    </Section>
  );
}

/** The home page's newest guides (the list seed is newest first), with a link to the hub. */
export function LatestGuides() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { articles } = useArticles(locale);
  const latest = articles.slice(0, LATEST_GUIDES);
  if (!latest.length) return null;
  return (
    <Section
      id="latest-guides"
      title={t('home.guidesTitle')}
      eyebrow={t('home.guidesEyebrow')}
      more={{ href: localePath(locale, GUIDES_PATH), label: t('guides.all') }}
      moreBelowOnMobile>
      <CardGrid>
        {latest.map((a) => (
          <GuideCard key={a._id} article={a} />
        ))}
      </CardGrid>
    </Section>
  );
}

/** The home page's guides seed, for its <PageHead>. */
export function latestGuidesSeed(locale: Locale) {
  return seedScript(articlesIndexSeedKey(locale));
}
